import { Component, OnInit } from '@angular/core';
import { GRADIENT_ICONS } from 'src/app/constants/imagePath';
import { ROUTES } from 'src/app/constants/Routes';


@Component({
  selector: 'app-connectiq',
  templateUrl: './connectiq.component.html',
  styleUrls: ['./connectiq.component.scss'],
  standalone: false
})
export class ConnectIQComponent implements OnInit {
  Routes = ROUTES;
  activeSection = 'overview';

  overviewHighlights = [
    { icon: 'bx-planet', val: 'Connected Anything', label: 'Vehicles, devices, industrial IoT, and digital service ecosystems — all on one platform.' },
    { icon: 'bx-cloud', val: 'Cloud-Agnostic', label: 'Runs on AWS, Azure, and Google Cloud with elastic cloud-native infrastructure.' },
    { icon: 'bx-check-shield', val: 'AIS-189 Ready', label: 'Automotive compliance-ready while flexible for IoT and IIoT deployments at scale.' },
  ];

  overviewCapabilities = [
    'Real-time vehicle tracking, geolocation, telemetry monitoring, and behavior intelligence.',
    'Secure connectivity and lifecycle management across vehicles, devices, sensors, and smart assets.',
    'Cloud-scale data collection, telemetry processing, storage, and analytics infrastructure.',
    'Unified integration across ECUs, telematics hardware, IoT gateways, mobile apps, and enterprise systems.',
  ];

  connectedEndpoints = [
    'Connected vehicles and telematics systems',
    'IoT and IIoT devices',
    'Smart assets and sensors',
    'Industrial equipment and machinery',
    'Mobile applications and digital services',
  ];

  products = [
    {
      id: 'vc-score',
      title: 'VC-SCORE',
      subtitle: 'Driver Intelligence & Analytics',
      desc: 'Advanced event detection and scoring engine enabling safe driving evaluation, usage-based insurance, driver coaching, and fleet performance benchmarking.',
      icon: 'bx-brain',
      iconImage: GRADIENT_ICONS.VC_SCORE,
      route: ROUTES.products_connectiq_vc_score,
      ctaLabel: 'Explore VC-SCORE',
      ctaRoute: ROUTES.products_connectiq_vc_score,
      tags: ['Scoring', 'Telematics', 'UBI'],
      keyPoints: [
        { icon: 'bx-alarm-exclamation', title: 'Real-Time Event Detection', desc: 'On-device and cloud detection of harsh braking, speeding, and cornering events.' },
        { icon: 'bx-shield-quarter', title: 'UBI Scoring Engine', desc: 'Actuarial-grade usage-based insurance scoring for underwriters and insurtech.' },
        { icon: 'bx-user-voice', title: 'Driver Coaching', desc: 'Personalised feedback and coaching modules to improve safe driving habits.' },
        { icon: 'bx-bar-chart-alt-2', title: 'Fleet Benchmarking', desc: 'Cross-fleet analytics with percentile ranking and performance trend analysis.' },
      ],
    },
    {
      id: 'miles',
      title: 'Varroc Miles',
      subtitle: 'Loyalty & Rewards Program',
      desc: 'Behaviour-based loyalty program that incentivizes safe and responsible driving through real-time miles tracking, campaign crediting, and redemption journeys.',
      icon: 'bx-coin-stack',
      iconImage: GRADIENT_ICONS.MILES,
      route: ROUTES.products_connectiq_miles,
      ctaLabel: 'Explore Varroc Miles',
      ctaRoute: ROUTES.products_connectiq_miles,
      tags: ['Rewards', 'Engagement', 'Loyalty'],
      keyPoints: [
        { icon: 'bx-car', title: 'Behaviour-Based Rewards', desc: 'Miles credited for safe driving events, eco-driving, and consistent device usage.' },
        { icon: 'bx-timer', title: 'Real-Time Crediting', desc: 'Instant miles calculation and wallet updates on every completed trip.' },
        { icon: 'bx-bullseye', title: 'Campaign Management', desc: 'Flexible campaign engine for OEMs, insurers, and fleet operators.' },
        { icon: 'bx-trophy', title: 'Gamified Leaderboards', desc: 'Competitive leaderboards and challenges to drive sustained user engagement.' },
      ],
    },
    {
      id: 'vstore',
      title: 'V-Store',
      subtitle: 'Marketplace & Redemption',
      desc: 'In-app rewards marketplace enabling Varroc Miles redemption through exclusive offers, campaign-based listings, and a seamless partner redemption experience.',
      icon: 'bx-store-alt',
      route: ROUTES.products_connectiq_vstore,
      ctaLabel: 'Explore V-Store',
      ctaRoute: ROUTES.products_connectiq_vstore,
      tags: ['Marketplace', 'Redemption', 'Offers'],
      keyPoints: [
        { icon: 'bx-gift', title: 'Miles-to-Offer Redemption', desc: 'Direct redemption of Varroc Miles for vouchers, services, and partner offers.' },
        { icon: 'bx-buildings', title: 'Partner Catalogue', desc: 'Curated partner network with exclusive offers and branded storefronts.' },
        { icon: 'bx-tag', title: 'Campaign-Based Listings', desc: 'Time-limited and targeted offer listings powered by the campaign engine.' },
        { icon: 'bx-credit-card', title: 'Seamless Checkout', desc: 'In-app checkout with OTP verification and instant digital delivery of rewards.' },
      ],
    },
    {
      id: 'cap',
      title: 'CAP',
      subtitle: 'Customer Adoption Program',
      desc: 'A unified customer-centric engagement framework that powers onboarding, adoption, engagement, analytics, monetization, and lifecycle management across connected mobility programs.',
      icon: 'bx-user-check',
      route: ROUTES.products_connectiq_cap,
      ctaLabel: 'Explore CAP',
      ctaRoute: ROUTES.products_connectiq_cap,
      tags: ['Adoption', 'Lifecycle', 'Engagement'],
      keyPoints: [
        { icon: 'bx-headphone', title: 'Support Layer', desc: 'Robust B2C L1 and B2B L2 support infrastructure for onboarding and issue resolution.' },
        { icon: 'bx-code-block', title: 'SDK-Driven Integration', desc: 'Integrates customer journeys and product touchpoints across mobile, web, and partner systems.' },
        { icon: 'bx-heart', title: 'Engagement & Loyalty', desc: 'Drives safer driving, higher engagement, and stronger return visits to OEM touchpoints.' },
        { icon: 'bx-bar-chart-alt-2', title: 'Tracking & Analytics', desc: 'Tracks adoption, behaviour, app engagement, and lifecycle performance for OEM programs.' },
      ],
    },
    {
      id: 'context-based-messaging',
      title: 'iQ360',
      subtitle: 'Journey Messaging & Orchestration',
      desc: 'A context-driven messaging layer for personalized customer communications across push, SMS, email, and in-app touchpoints.',
      icon: 'bx-message-dots',
      route: ROUTES.products_connectiq_context_based_messaging,
      ctaLabel: 'Explore Messaging',
      ctaRoute: ROUTES.products_connectiq_context_based_messaging,
      tags: ['Messaging', 'Engagement', 'Journeys'],
      keyPoints: [
        { icon: 'bx-bell', title: 'Contextual Notifications', desc: 'Trigger messages based on customer status, vehicle events, and lifecycle milestones.' },
        { icon: 'bx-mail-send', title: 'Multi-Channel Reach', desc: 'Coordinate engagement across push notifications, email, SMS, and in-app experiences.' },
        { icon: 'bx-slider-alt', title: 'Journey Controls', desc: 'Build targeted journeys for onboarding, renewal, retention, and product education.' },
        { icon: 'bx-time-five', title: 'Content Placeholder', desc: 'Microsite structure is ready and detailed product content can be added next.' },
      ],
    },
    {
      id: 'oasis',
      title: 'Oasis',
      subtitle: 'Connected OTA Platform',
      desc: 'A unified automotive cloud ecosystem that consolidates vehicle data, OEM digital services, and third-party integrations into a single managed platform.',
      icon: 'bx-cloud',
      iconImage: GRADIENT_ICONS.OASIS,
      route: ROUTES.products_connectiq_oasis,
      ctaLabel: 'Explore Oasis',
      ctaRoute: ROUTES.products_connectiq_oasis,
      tags: ['Cloud', 'Ecosystem', 'OEM'],
      keyPoints: [
        { icon: 'bx-cloud-upload', title: 'Unified Data Layer', desc: 'Consolidates vehicle, service, and partner data into one governed cloud environment.' },
        { icon: 'bx-link', title: 'Third-Party Integrations', desc: 'Pre-built connectors for mapping, payment, insurance, and service providers.' },
        { icon: 'bx-shield', title: 'Governance & Compliance', desc: 'Built-in data governance with GDPR, AIS-140, and global privacy compliance.' },
        { icon: 'bx-expand', title: 'Elastic Scalability', desc: 'Auto-scales from hundreds to millions of connected vehicles without re-architecture.' },
      ],
    },
    {
      id: 'iiot',
      title: 'IIOT',
      subtitle: 'Industrial IoT Connectivity',
      desc: 'Industrial-grade IoT connectivity layer bridging field devices, industrial PLCs, and enterprise systems with real-time monitoring and remote control.',
      icon: 'bx-git-branch',
      route: ROUTES.products_connectiq_iiot,
      ctaLabel: 'Explore IIOT',
      ctaRoute: ROUTES.products_connectiq_iiot,
      tags: ['Industrial IoT', 'SCADA', 'Edge'],
      keyPoints: [
        { icon: 'bx-chip', title: 'Field Device Management', desc: 'Remote configuration, monitoring, and control of sensors, PLCs, and gateways.' },
        { icon: 'bx-time-five', title: 'Real-Time Monitoring', desc: 'Sub-second telemetry streaming from industrial assets to cloud dashboards.' },
        { icon: 'bx-cube', title: 'Edge Processing', desc: 'On-premise edge computing for latency-sensitive industrial control loops.' },
        { icon: 'bx-link-alt', title: 'OPC-UA & Modbus Support', desc: 'Native support for industrial communication protocols and legacy field devices.' },
      ],
    },
    {
      id: 'vehicle-app-store',
      title: 'Vehicle App Store',
      subtitle: 'In-Vehicle App Marketplace',
      desc: 'An in-vehicle app marketplace enabling OEMs and third-party developers to publish, distribute, and monetize applications directly to connected vehicles.',
      icon: 'bx-store-alt',
      iconImage: GRADIENT_ICONS.VEHICLE_APP_STORE,
      route: ROUTES.products_connectiq_vehicle_app_store,
      ctaLabel: 'Explore Vehicle App Store',
      ctaRoute: ROUTES.products_connectiq_vehicle_app_store,
      tags: ['Marketplace', 'Apps', 'OEM'],
      keyPoints: [
        { icon: 'bx-code-alt', title: 'Developer SDK', desc: 'Open SDK and developer portal for building certified in-vehicle applications.' },
        { icon: 'bx-check-shield', title: 'App Certification', desc: 'Automated safety and security review pipeline for all marketplace submissions.' },
        { icon: 'bx-dollar-circle', title: 'Monetization Engine', desc: 'Subscription, one-time purchase, and freemium business models for app revenue.' },
        { icon: 'bx-refresh', title: 'OTA Distribution', desc: 'Over-the-air app installation, updates, and rollback for connected vehicles.' },
      ],
    },
    {
      id: 'secureiq',
      title: 'SecureIQ',
      subtitle: 'PKI-Driven Trust, Certificate Lifecycle & Zero-Trust Security',
      desc: 'SecureIQ enables OEMs, mobility providers, and industrial enterprises to securely scale connected ecosystems with PKI-driven trust, certificate lifecycle automation, and zero-trust cybersecurity architecture.',
      icon: 'bx-shield-quarter',
      route: ROUTES.products_connectiq_secureiq,
      ctaLabel: 'Explore SecureIQ',
      ctaRoute: ROUTES.products_connectiq_secureiq,
      tags: ['PKI', 'Security', 'Zero-Trust'],
      keyPoints: [
        { icon: 'bx-chip', title: 'Device Identity Management', desc: 'Unique cryptographic digital identity provisioned for every connected device, ECU, and gateway at factory or field stage.' },
        { icon: 'bx-refresh', title: 'Certificate Lifecycle', desc: 'Automated issuance, rotation, renewal, and revocation with CRL/OCSP validation and fleet-wide orchestration.' },
        { icon: 'bx-link-alt', title: 'Mutual Authentication (mTLS)', desc: 'Device-to-cloud and device-to-device trust establishment using certificate-based mutual TLS handshakes.' },
        { icon: 'bx-lock-alt', title: 'Zero-Trust Architecture', desc: 'Verify every device, service, and API with continuous re-authentication and least-privilege access control.' },
      ],
    },
    {
      id: 'subscription-management',
      title: 'Subscription Management',
      subtitle: 'Plan Lifecycle & Billing',
      desc: 'A seamless subscription lifecycle module that lets users activate, renew, upgrade, and track plans while ensuring uninterrupted access to connected vehicle features.',
      icon: 'bx-badge-check',
      ctaLabel: 'Talk to Our Team',
      ctaRoute: ROUTES.contact,
      tags: ['Subscriptions', 'Billing', 'Retention'],
      keyPoints: [
        { icon: 'bx-list-check', title: 'Plan Activation & Renewal', desc: 'Supports simple plan selection, activation, renewal, upgrades, and downgrade to freemium flows.' },
        { icon: 'bx-bell', title: 'Automated Renewal Reminders', desc: 'Sends timely reminders across app, email, and notification channels to improve renewals.' },
        { icon: 'bx-receipt', title: 'Billing & Invoice Management', desc: 'Secure payment renewals with invoice generation, GST details, settlement, and reconciliation tracking.' },
        { icon: 'bx-lock-open-alt', title: 'Feature Access Control', desc: 'Enables or disables premium features in real time based on the active subscription plan.' },
      ],
    },
  ];

  ngOnInit() {}


  scrollTo(sectionId: string) {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.activeSection = sectionId;
  }

  private setupIntersectionObserver() {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) this.activeSection = entry.target.id;
        });
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    this.products.forEach(p => {
      const el = document.getElementById(p.id);
      if (el) observer.observe(el);
    });
  }
}

